import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { nanoid } from 'nanoid';
import type { 
  Achievement, 
  UserAchievement, 
  AchievementProgress, 
  AchievementRequirement,
  EmotionalStateData,
  SensoryAnchor,
  StoryEvidence
} from '@/types/achievements';
import { achievementDefinitions } from '@/data/achievementDefinitions';

interface AchievementContextType {
  achievements: Achievement[];
  userAchievements: UserAchievement[];
  achievementProgress: AchievementProgress[];
  totalPoints: number;
  currentLevel: number;
  streakDays: number;
  isLoading: boolean;
  
  // Achievement evaluation
  evaluateAchievements: (userProgress: Partial<AchievementRequirement>) => void;
  unlockAchievement: (achievementId: string, evidence?: any) => void;
  
  // Progress tracking
  trackProgress: (stepId: string, data?: any) => void;
  trackEmotionalState: (stateData: EmotionalStateData) => void;
  trackSensoryAnchor: (anchor: Omit<SensoryAnchor, 'id' | 'user_id' | 'created_at'>) => void;
  trackStoryEvidence: (story: Omit<StoryEvidence, 'id' | 'user_id' | 'created_at'>) => void;
  
  // Post-Flight Community tracking
  trackFlightReport: (reportData: any) => void;
  trackCommunityProject: (projectData: any) => void;
  trackRippleEffect: (rippleData: any) => void;
  
  // Social features
  shareAchievement: (achievementId: string, level: 'private' | 'team' | 'organization' | 'public') => void;
  mentorValidateAchievement: (userAchievementId: string) => void;
  
  // Analytics
  getProgressByCategory: (category: string) => AchievementProgress[];
  getPredictedCompletionDates: () => { [achievementId: string]: Date };
}

const AchievementContext = createContext<AchievementContextType | null>(null);

export function useAchievements() {
  const context = useContext(AchievementContext);
  if (!context) {
    throw new Error('useAchievements must be used within an AchievementProvider');
  }
  return context;
}

interface AchievementProviderProps {
  children: React.ReactNode;
  userId: string;
}

export function AchievementProvider({ children, userId }: AchievementProviderProps) {
  const [currentUserProgress, setCurrentUserProgress] = useState<Partial<AchievementRequirement>>({});
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch user achievements
  const { data: userAchievements = [], isLoading: achievementsLoading } = useQuery<UserAchievement[]>({
    queryKey: ['achievements/user', userId],
    queryFn: async () => {
      try {
        const response = await apiRequest('GET', 'achievements/user');
        const data = await response.json();
        // Handle the response format from our API
        if (data && typeof data === 'object') {
          // Check if authentication is required
          if (data.requiresAuthentication) {
            console.log('[Achievements] Authentication required for achievements');
            return [];
          }
          if (Array.isArray(data)) {
            return data; // Direct array format
          }
          if (data.achievements && Array.isArray(data.achievements)) {
            return data.achievements; // Object with achievements array
          }
          if (data.unlockedAchievements && Array.isArray(data.unlockedAchievements)) {
            return data.unlockedAchievements; // Alternative property name
          }
        }
        console.warn('[Achievements] Unexpected response format, returning empty array:', data);
        return [];
      } catch (error) {
        console.error('[Achievements] Error fetching user achievements:', error);
        return [];
      }
    },
    // Always try to fetch achievements, even without userId for unauthenticated state
    enabled: true,
  });

  // Fetch achievement progress
  const { data: achievementProgress = [], isLoading: progressLoading } = useQuery<AchievementProgress[]>({
    queryKey: ['achievements/progress', userId],
    queryFn: async () => {
      try {
        const response = await apiRequest('GET', 'achievements/progress');
        const data = await response.json();
        if (data && typeof data === 'object') {
          // Check if authentication is required
          if (data.requiresAuthentication) {
            console.log('[Achievements] Authentication required for progress');
            return [];
          }
          if (Array.isArray(data)) {
            return data;
          }
          if (data.progress && Array.isArray(data.progress)) {
            return data.progress;
          }
        }
        console.warn('[Achievements] Unexpected progress response format, returning empty array:', data);
        return [];
      } catch (error) {
        console.error('[Achievements] Error fetching achievement progress:', error);
        return [];
      }
    },
    // Always try to fetch progress, even without userId for unauthenticated state
    enabled: true,
  });

  // Fetch user stats
  const { data: userStats = { totalPoints: 0, currentLevel: 1, streakDays: 0 } } = useQuery({
    queryKey: ['achievements/stats', userId],
    queryFn: async () => {
      try {
        const response = await apiRequest('GET', 'achievements/stats');
        const data = await response.json();
        if (data && typeof data === 'object') {
          // Check if authentication is required
          if (data.requiresAuthentication) {
            console.log('[Achievements] Authentication required for stats');
            return { totalPoints: 0, currentLevel: 1, streakDays: 0 };
          }
          if (data.stats && typeof data.stats === 'object') {
            return data.stats;
          }
          // Handle direct stats format
          if (data.totalPoints !== undefined || data.currentLevel !== undefined) {
            return {
              totalPoints: data.totalPoints || 0,
              currentLevel: data.currentLevel || 1,
              streakDays: data.streakDays || 0
            };
          }
        }
        console.warn('[Achievements] Unexpected stats response format, returning defaults:', data);
        return { totalPoints: 0, currentLevel: 1, streakDays: 0 };
      } catch (error) {
        console.error('[Achievements] Error fetching user stats:', error);
        return { totalPoints: 0, currentLevel: 1, streakDays: 0 };
      }
    },
    // Always try to fetch stats, even without userId for unauthenticated state
    enabled: true,
  });

  // Unlock achievement mutation
  const unlockMutation = useMutation({
    mutationFn: async ({ achievementId, evidence }: { achievementId: string; evidence?: any }) => {
      return apiRequest('POST', 'achievements/unlock', {
        user_id: userId,
        achievement_id: achievementId,
        evidence_data: evidence,
        unlocked_at: new Date().toISOString()
      });
    },
    onSuccess: (data, variables) => {
      const achievement = achievementDefinitions.find(a => a.id === variables.achievementId);
      if (achievement) {
        toast({
          title: "Achievement Unlocked! 🎉",
          description: achievement.unlock_message || `You've earned "${achievement.name}"!`,
          duration: 5000,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['achievements/user', userId] });
      queryClient.invalidateQueries({ queryKey: ['achievements/stats', userId] });
    }
  });

  // Progress tracking mutation
  const progressMutation = useMutation({
    mutationFn: async (progressData: any) => {
      return apiRequest('POST', 'achievements/track-progress', {
        user_id: userId,
        ...progressData,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['achievements/progress', userId] });
    }
  });

  // Achievement evaluation engine
  const evaluateAchievements = (userProgress: Partial<AchievementRequirement>) => {
    setCurrentUserProgress(prev => ({ ...prev, ...userProgress }));
    
    // Check each achievement against current progress
    achievementDefinitions.forEach(achievement => {
      // Skip if already unlocked
      const alreadyUnlocked = userAchievements.some(ua => ua.achievement_id === achievement.id);
      if (alreadyUnlocked) return;

      // Check prerequisites
      if (achievement.prerequisite_achievements) {
        const prerequisitesMet = achievement.prerequisite_achievements.every(prereqId =>
          userAchievements.some(ua => ua.achievement_id === prereqId)
        );
        if (!prerequisitesMet) return;
      }

      // Evaluate requirements
      const requirementsMet = checkRequirements(achievement.requirements, { ...currentUserProgress, ...userProgress });
      
      if (requirementsMet) {
        unlockAchievement(achievement.id, { userProgress, timestamp: new Date() });
      }
    });
  };

  // Requirements checking logic
  const checkRequirements = (requirements: AchievementRequirement, userProgress: Partial<AchievementRequirement>): boolean => {
    return Object.entries(requirements).every(([key, requiredValue]) => {
      const userValue = userProgress[key as keyof AchievementRequirement];
      
      if (typeof requiredValue === 'boolean') {
        return userValue === requiredValue;
      }
      
      if (typeof requiredValue === 'number') {
        return (userValue as number) >= requiredValue;
      }
      
      return false;
    });
  };

  // Unlock achievement
  const unlockAchievement = (achievementId: string, evidence?: any) => {
    unlockMutation.mutate({ achievementId, evidence });
  };

  // Progress tracking functions
  const trackProgress = (stepId: string, data?: any) => {
    const progressUpdate: Partial<AchievementRequirement> = {
      [`step${stepId}_completed`]: true,
      ...data
    };
    
    // Check if all 6 steps are complete for helicopter unlock
    if (stepId === '6') {
      progressUpdate.all_components_unlocked = true;
    }
    
    progressMutation.mutate({ type: 'step_completion', step_id: stepId, data });
    evaluateAchievements(progressUpdate);
  };

  // Post-Flight Community tracking functions
  const trackFlightReport = (reportData: any) => {
    const progressUpdate: Partial<AchievementRequirement> = {
      flight_reports_submitted: (currentUserProgress.flight_reports_submitted || 0) + 1,
      life_balance_consistency: reportData.lifeBalance >= 7 ? 
        (currentUserProgress.life_balance_consistency || 0) + 10 : 
        currentUserProgress.life_balance_consistency || 0,
      money_progress_consistency: reportData.moneyProgress >= 7 ? 
        (currentUserProgress.money_progress_consistency || 0) + 10 : 
        currentUserProgress.money_progress_consistency || 0,
      joy_level_consistency: reportData.joyLevel >= 7 ? 
        (currentUserProgress.joy_level_consistency || 0) + 10 : 
        currentUserProgress.joy_level_consistency || 0
    };
    
    progressMutation.mutate({ type: 'flight_report', data: reportData });
    evaluateAchievements(progressUpdate);
  };

  const trackCommunityProject = (projectData: any) => {
    const progressUpdate: Partial<AchievementRequirement> = {
      community_projects_participated: (currentUserProgress.community_projects_participated || 0) + 1,
      values_alignment_score: Math.max(
        currentUserProgress.values_alignment_score || 0,
        projectData.valuesAlignment || 0
      ),
      money_capability_demonstration: projectData.moneyCapabilityShown || false,
      collaborative_leadership: projectData.leadershipDemonstrated || false
    };
    
    progressMutation.mutate({ type: 'community_project', data: projectData });
    evaluateAchievements(progressUpdate);
  };

  const trackRippleEffect = (rippleData: any) => {
    const progressUpdate: Partial<AchievementRequirement> = {
      ripple_effects_tracked: (currentUserProgress.ripple_effects_tracked || 0) + 1
    };

    // Track specific ripple categories
    if (rippleData.category === 'hobbies') {
      progressUpdate.hobbies_impact_documented = (currentUserProgress.hobbies_impact_documented || 0) + 1;
    }
    if (rippleData.category === 'relationships') {
      progressUpdate.relationship_quality_improvements = (currentUserProgress.relationship_quality_improvements || 0) + 1;
    }
    if (rippleData.category === 'community') {
      progressUpdate.community_contributions_measured = (currentUserProgress.community_contributions_measured || 0) + 1;
    }

    // Check for rich qualitative stories and comprehensive metrics
    if (rippleData.qualitativeStory && rippleData.qualitativeStory.length > 100) {
      progressUpdate.qualitative_stories_rich = true;
    }
    if (rippleData.quantitativeMetrics && 
        rippleData.quantitativeMetrics.peopleImpacted > 0 && 
        rippleData.quantitativeMetrics.timeInvested > 0 && 
        rippleData.quantitativeMetrics.valueCreated) {
      progressUpdate.quantitative_metrics_comprehensive = true;
    }
    
    progressMutation.mutate({ type: 'ripple_effect', data: rippleData });
    evaluateAchievements(progressUpdate);
  };

  const trackEmotionalState = (stateData: EmotionalStateData) => {
    const progressUpdate: Partial<AchievementRequirement> = {};
    
    if (stateData.parasympathetic_activation) {
      progressUpdate.parasympathetic_activation_count = (currentUserProgress.parasympathetic_activation_count || 0) + 1;
    }
    
    if (stateData.engagement_quality_score >= 80) {
      progressUpdate.emotional_state_mastery = true;
    }
    
    progressUpdate.biometric_data_points = (currentUserProgress.biometric_data_points || 0) + 1;
    
    progressMutation.mutate({ type: 'emotional_state', data: stateData });
    evaluateAchievements(progressUpdate);
  };

  const trackSensoryAnchor = (anchor: Omit<SensoryAnchor, 'id' | 'user_id' | 'created_at'>) => {
    const progressUpdate: Partial<AchievementRequirement> = {
      sensory_anchor_count: (currentUserProgress.sensory_anchor_count || 0) + 1
    };
    
    progressMutation.mutate({ type: 'sensory_anchor', data: anchor });
    evaluateAchievements(progressUpdate);
  };

  const trackStoryEvidence = (story: Omit<StoryEvidence, 'id' | 'user_id' | 'created_at'>) => {
    const progressUpdate: Partial<AchievementRequirement> = {
      story_completion_count: (currentUserProgress.story_completion_count || 0) + 1
    };
    
    if (story.voice_recording_url) {
      progressUpdate.voice_recording_count = (currentUserProgress.voice_recording_count || 0) + 1;
    }
    
    if (story.photo_urls && story.photo_urls.length > 0) {
      progressUpdate.photo_upload_count = (currentUserProgress.photo_upload_count || 0) + story.photo_urls.length;
    }
    
    if (story.vulnerability_level >= 7) {
      progressUpdate.vulnerability_demonstration = true;
    }
    
    if (story.multi_modal_integration_score >= 80) {
      progressUpdate.multi_modal_integration = true;
    }
    
    progressUpdate.wisdom_synthesis_score = Math.max(
      currentUserProgress.wisdom_synthesis_score || 0,
      story.wisdom_extraction_quality
    );
    
    progressMutation.mutate({ type: 'story_evidence', data: story });
    evaluateAchievements(progressUpdate);
  };

  // Social features
  const shareAchievement = (achievementId: string, level: 'private' | 'team' | 'organization' | 'public') => {
    apiRequest('POST', 'achievements/share', {
      user_id: userId,
      achievement_id: achievementId,
      sharing_level: level
    });
  };

  const mentorValidateAchievement = (userAchievementId: string) => {
    apiRequest('POST', `achievements/validate/${userAchievementId}`, {
      mentor_validated: true
    });
  };

  // Analytics functions
  const getProgressByCategory = (category: string): AchievementProgress[] => {
    const categoryAchievements = achievementDefinitions
      .filter(a => a.category === category)
      .map(a => a.id);
    
    return achievementProgress.filter(p => categoryAchievements.includes(p.achievement_id));
  };

  const getPredictedCompletionDates = (): { [achievementId: string]: Date } => {
    const predictions: { [achievementId: string]: Date } = {};
    
    achievementProgress.forEach(progress => {
      if (progress.estimated_completion_date) {
        predictions[progress.achievement_id] = progress.estimated_completion_date;
      }
    });
    
    return predictions;
  };

  // Update engagement tracking
  useEffect(() => {
    const engagementUpdate: Partial<AchievementRequirement> = {
      days_active: userStats.streakDays > 0 ? (currentUserProgress.days_active || 0) + 1 : 1,
      streak_days: userStats.streakDays
    };
    
    evaluateAchievements(engagementUpdate);
  }, [userStats.streakDays]);

  const value: AchievementContextType = {
    achievements: achievementDefinitions,
    userAchievements,
    achievementProgress,
    totalPoints: userStats.totalPoints,
    currentLevel: userStats.currentLevel,
    streakDays: userStats.streakDays,
    isLoading: achievementsLoading || progressLoading,
    
    evaluateAchievements,
    unlockAchievement,
    
    trackProgress,
    trackEmotionalState,
    trackSensoryAnchor,
    trackStoryEvidence,
    
    trackFlightReport,
    trackCommunityProject,
    trackRippleEffect,
    
    shareAchievement,
    mentorValidateAchievement,
    
    getProgressByCategory,
    getPredictedCompletionDates
  };

  return (
    <AchievementContext.Provider value={value}>
      {children}
    </AchievementContext.Provider>
  );
}